import { ChevronDown, ChevronUp, ChevronsUpDown, ChevronLeft, ChevronRight, RotateCcw, Search } from 'lucide-react';

/**
 * Shared list-page primitives.
 * Every list page in Kollez uses these building blocks so the UX is identical everywhere:
 *   · <FilterBar> with search + one or more selects + reset button
 *   · <SortHeader> on each column for server-side sort
 *   · <Pagination> under the table
 *
 * The parent page keeps `TableQueryParams` in state and passes callbacks in.
 */

export function SortHeader({
  label,
  field,
  currentSort,
  currentOrder,
  onSort,
  className,
}: {
  label: string;
  field: string;
  currentSort: string | null;
  currentOrder: 'asc' | 'desc';
  onSort: (f: string) => void;
  className?: string;
}) {
  const active = currentSort === field;
  return (
    <th className={className}>
      <button
        type="button"
        onClick={() => onSort(field)}
        className="flex items-center gap-1 hover:text-accent"
        style={{ background: 'none', border: 0, padding: 0, cursor: 'pointer', font: 'inherit', color: 'inherit' }}
      >
        {label}
        {active ? (currentOrder === 'asc' ? <ChevronUp size={12} /> : <ChevronDown size={12} />) : <ChevronsUpDown size={12} className="opacity-40" />}
      </button>
    </th>
  );
}

export function TableSearch({ value, onChange, placeholder }: { value: string; onChange: (v: string) => void; placeholder?: string }) {
  return (
    <div className="input-group fb-search">
      <Search size={14} />
      <input
        className="input"
        placeholder={placeholder ?? 'Search'}
        value={value}
        onChange={(e) => onChange(e.target.value)}
      />
    </div>
  );
}

export function ResetButton({ onReset, disabled }: { onReset: () => void; disabled?: boolean }) {
  return (
    <button
      type="button"
      className="btn btn-ghost btn-sm"
      onClick={onReset}
      disabled={disabled}
      title="Reset filters"
    >
      <RotateCcw size={12} />Reset
    </button>
  );
}

export function Pagination({
  page,
  totalPages,
  total,
  limit,
  onPage,
  onLimit,
}: {
  page: number;
  totalPages: number;
  total: number;
  limit: number;
  onPage: (p: number) => void;
  onLimit?: (n: number) => void;
}) {
  const start = total === 0 ? 0 : (page - 1) * limit + 1;
  const end = Math.min(page * limit, total);
  return (
    <div className="flex items-center gap-3 px-4 py-3 text-sm border-t border-line-tertiary flex-wrap">
      <span className="text-secondary">
        {total === 0 ? 'No results' : <>Showing {start.toLocaleString('en-IN')}–{end.toLocaleString('en-IN')} of {total.toLocaleString('en-IN')}</>}
      </span>
      {onLimit && (
        <label className="flex items-center gap-2 ml-auto">
          <span className="cell-sub">Rows per page</span>
          <select
            className="select"
            style={{ width: 'auto' }}
            value={limit}
            onChange={(e) => onLimit(Number(e.target.value))}
          >
            {[10, 25, 50, 100].map((n) => <option key={n} value={n}>{n}</option>)}
          </select>
        </label>
      )}
      <div className={`flex items-center gap-1 ${onLimit ? '' : 'ml-auto'}`}>
        <button className="btn btn-ghost btn-sm" onClick={() => onPage(page - 1)} disabled={page <= 1} aria-label="Previous page">
          <ChevronLeft size={14} />
        </button>
        <span className="px-2">
          Page {page} of {totalPages}
        </span>
        <button className="btn btn-ghost btn-sm" onClick={() => onPage(page + 1)} disabled={page >= totalPages} aria-label="Next page">
          <ChevronRight size={14} />
        </button>
      </div>
    </div>
  );
}
